import React from 'react';
import {webflowizeStyleSheet} from '@packages/systems/designer/styles';
import {fontVariations} from '@packages/systems/core/utils/FontUtils';
import {Map, List} from 'immutable';
import type {IterableElement} from 'type-fest';
import {REGISTERED_AXIS_WGHT} from '@packages/systems/core/constants/Fonts';
import {SiteDataStateType} from '@packages/systems/designer/site-data';

const nullChar = String.fromCharCode(0);
const weightSplitChar = '-';

const formatFromShortcode = (fileExtension: string) => {
  // all other font file extensions happen to be a valid name for the format
  // (e.g., woff, woff2, svg).
  const fileExtensionToFormatMap: Record<string, string> = {
    eot: 'embedded-opentype',
    ttf: 'truetype',
    otf: 'opentype',
  };
  return fileExtensionToFormatMap[fileExtension.toLowerCase()] || fileExtension;
};

const fontSource = (url: string, format: string) =>
  `url(${url}) format('${format}')`;

/**
 * Formats the font weight that's included in the font key when
 * generating font face declarations. The font will be formatted as
 * a specific weight (e.g. 400), or a range of supported weights
 * (e.g. '400-700')
 *
 * FIXME: Dedupe this from `cssUtils.js`
 */
function formatFontWeightForKey(
  font: IterableElement<SiteDataStateType['customFonts']>
) {
  const isVariableFont = font.axes && font.axes.length > 0;

  // Check to see if the variable font has a 'wght' axis. If it does,
  // we'll use the axis min and max values as the 'font-weight' range
  // for the @font-face declaration
  const wghtAxis = isVariableFont
    ? font.axes.find((axis) => axis.tag === REGISTERED_AXIS_WGHT)
    : null;

  if (wghtAxis) {
    // e.g. '400-700'
    return `${wghtAxis.min}${weightSplitChar}${wghtAxis.max}`;
  }

  // If it's a static font, or a variable font without a 'wght' axis,
  // we'll return the font weight as-is since we don't need to specify a range.
  // e.g. 400
  return font.weight;
}

const generateFontFaceDeclarations = (
  fonts: SiteDataStateType['customFonts']
) => {
  return fonts
    .reduce((fs, font) => {
      // Create a map of font descriptors to font sources:
      // e.g., `Ubuntu Mono:normal:400` => ['url(abc.woff) format('woff')', 'url(abc.ttf) format('truetype')']
      //
      // This groups font declarations by name/weight/style, and include all
      // applicable font sources for a given font, which is how we support
      // multiple font formats, along with multiple styles and weights.

      const family = font.fontFamily;
      const weight = formatFontWeightForKey(font);
      const fontStyle = font.italic ? 'italic' : 'normal';
      const fontFormat = formatFromShortcode(font.format);
      const url = font.file.cdnUrl;

      // we create a key with the key font characteristics; use nullChar as
      // delimeter, because it is unlikely to be used in a valid font-face name.
      // AFAIK, we have no validation requirements on font names.
      const key = [family, weight, fontStyle].join(nullChar);
      return fs.update(key, List(), (sources) =>
        sources.push(fontSource(url, fontFormat))
      );
    }, Map<string, List<string>>())
    .map((sources, fontInfo) => {
      // Then, we map over each font variant and return a declaration for it
      // that includes all applicable file formats:

      const [family, weight, fontStyle] = fontInfo.split(nullChar);

      // The weight may be a range if it's a variable font with a 'wght' axis
      // If it is a range, the secondaryWeight value will be defined.
      const [defaultWeight, secondaryWeight] = (weight || '').split(
        weightSplitChar
      );

      // https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-weight
      // Standard: font-weight: 400;
      // Range: font-weight: 400 700;
      const fontWeight = secondaryWeight
        ? `${defaultWeight} ${secondaryWeight}`
        : defaultWeight;

      return `
      @font-face {
        font-family: '${family}';
        font-weight: ${fontWeight};
        font-style: ${fontStyle};
        src: ${sources.join(', ')};
      }`;
    })
    .toArray();
};

export default function CustomFonts({
  fonts,
  renderStyles = false,
}: {
  fonts: SiteDataStateType['customFonts'];
  renderStyles?: boolean;
}) {
  const styleRef = React.useRef<HTMLStyleElement | null>(null);
  const [styles, setStyles] = React.useState<string>('');

  const loadFonts = async (styleElement: HTMLStyleElement | null) => {
    if (fonts && styleElement) {
      const WebFont = await require('webfontloader');
      const document = styleElement.ownerDocument;
      const context = document.defaultView;
      const styleSheet = webflowizeStyleSheet(document, styleElement);

      // Dynamically insert font declarations in the CSSOM:
      const cssRules = generateFontFaceDeclarations(fonts);
      cssRules.forEach((rule, index) => styleSheet.insertRule(rule, index));

      // set styles to display within style tag for custom code
      setStyles(cssRules.join('\n'));

      // tell WebFont to prefetch the fonts
      const families = fontVariations(fonts);

      WebFont.load({
        custom: {families},
        context,
      });
    }
  };

  React.useEffect(() => {
    loadFonts(styleRef.current);
  }, [fonts]);

  return (
    <style ref={styleRef} data-automation-id="custom-fonts">
      {renderStyles && styles}
    </style>
  );
}

export const FontStyles = React.memo(
  ({fonts}: {fonts: SiteDataStateType['customFonts']}) => (
    <style>{generateFontFaceDeclarations(fonts)}</style>
  )
);
